import React, { FC, useState } from 'react';

import { Tabs } from '@components/common';
import { Subtitle } from '@components/ScreenerResults/ScreenerChart/components/shared';
import { api } from '@components/SurveyBuilder';
import { compact } from '@components/utils';

import { Paths } from './components/Paths';
import { ResponsesTable } from './components/ResponsesTable';
import { Screens } from './components/Screens';
import { Summary } from './components/Summary';
import { Timelines } from './components/Timelines';

type TabValues = 'table' | 'paths' | 'timelines' | 'screens';

interface Props {
  prototypeTestId: number;
  answers: ScreenerResponseAnswerValue<'prototype_test'>[];
  total?: number;
}

export const PrototypeTestResponse: FC<Props> = ({ answers, prototypeTestId, total }) => {
  const [activeTab, setActiveTab] = useState<TabValues>('table');

  const validAnswers = compact(answers) as NonNullable<ScreenerResponseAnswerValue<'prototype_test'>>[];

  const { data, isLoading } = api.useGetSurveyBuilderPrototypeTestBlockableQuery(
    { id: prototypeTestId },
    { skip: !prototypeTestId }
  );

  if (validAnswers.length === 0 || isLoading) {
    return null;
  }

  return (
    <div>
      <Subtitle type='Prototype test' total={total} />

      <Summary answers={validAnswers} testType={data?.settings?.test_type} />

      <div className='flex items-center justify-between'>
        <Tabs<TabValues>
          current={activeTab}
          tabs={['table', 'paths', 'timelines', 'screens']}
          labels={{ table: 'Table', paths: 'Paths', timelines: 'Timelines', screens: 'Screens' }}
          onSelect={setActiveTab}
          className='mb-4'
        />
      </div>

      {activeTab === 'table' && (
        <ResponsesTable testType={data?.settings?.test_type} prototypeTestId={prototypeTestId} answers={validAnswers} />
      )}

      {activeTab === 'paths' && <Paths prototypeTestId={prototypeTestId} answers={validAnswers} />}

      {activeTab === 'timelines' && <Timelines answers={validAnswers} />}

      {activeTab === 'screens' && <Screens answers={validAnswers} prototypeTestId={prototypeTestId} />}
    </div>
  );
};
